import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FolderOpen,
  Database,
  ArrowRight,
  FileText,
  Search,
  MapPin,
  Power,
  PowerOff,
  Activity,
  TrendingUp,
  AlertCircle,
  Megaphone,
  MessagesSquare,
  ShieldCheck,
  Sliders,
  Palette,
  LineChart,
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { dashboardApi } from "../services/dashboardApi";
import { SECTIONS, CONVERSATIONS, ANALYTICS } from "../utils/roles";
import SuperAdminHomepage from "./SuperAdminHomepage";

const Homepage = () => {
  const { translate } = useLanguage();
  const { isPlatformOnlySuperAdmin, isAdmin, isEffectivelyAdmin, hasAllAccess } = useAuth();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    // Only all-access users see the overview dashboard; the backend gates
    // the stats endpoints to admin, so nobody else fetches them.
    if (isPlatformOnlySuperAdmin() || !hasAllAccess()) return;
    const abortController = new AbortController();
    (async () => {
      try {
        const stats = await dashboardApi.getAdminStats();
        if (!abortController.signal.aborted) {
          setData(stats);
        }
      } catch (err) {
        if (!abortController.signal.aborted) {
          console.error("Error fetching dashboard data:", err);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    })();
    return () => abortController.abort();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (isPlatformOnlySuperAdmin()) {
    return <SuperAdminHomepage />;
  }

  // Non-all-access users get a personalized landing: no cross-section
  // counts, no Recent Files. The real boundary is the backend's admin-only
  // gate on /dashboard/user-stats; this is the UX for everyone else.
  if (!hasAllAccess()) {
    return <PersonalizedLanding />;
  }

  const productsVectors = data?.products_vectors || 0;
  const schedulerRunning = data?.scheduler?.running || false;
  const ingestionStatus = data?.ingestion || {};
  const stores = data?.stores || { total: 0, active: 0, inactive: 0 };
  const filesByCollection = data?.files_by_collection || {};
  const filesByType = data?.files_by_type || {};
  const recentFiles = data?.recent_files || [];
  const warnings = data?.warnings || [];

  const collectionLabels = {
    general_information: translate("generalInformation") || "General Information",
    campaigns: translate("campaigns") || "Campaigns",
    internal_procedures: translate("internalProcedures") || "Internal Procedures",
    internal_chat: translate("internalChat") || "Internal Chat",
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  };

  const totalFiles = Object.values(filesByCollection).reduce((a, b) => a + b, 0);
  const maxFilesByCol = Math.max(...Object.values(filesByCollection), 1);
  const maxFilesByType = Math.max(...Object.values(filesByType), 1);

  return (
    <div className="page-enter">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold" style={{ color: "var(--color-text-primary)" }}>
          {translate("welcomeBack") || "Welcome back"}
        </h1>
        <p className="text-sm mt-1" style={{ color: "var(--color-text-secondary)" }}>
          {translate("dashboardDescription") || "Overview of your AI Agent system"}
        </p>
      </div>

      {warnings.length > 0 && (
        <div
          className="flex items-start gap-2 rounded-lg px-4 py-3 mb-6 text-xs"
          style={{ backgroundColor: "rgba(234,179,8,0.08)", color: "var(--color-text-secondary)" }}
        >
          <AlertCircle size={14} className="mt-0.5 flex-shrink-0" style={{ color: "#eab308" }} />
          <div>
            {warnings.map((w, i) => (
              <p key={i}>{w}</p>
            ))}
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-6 animate-pulse">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="card p-5">
                <div className="h-3 w-20 rounded bg-[var(--color-skeleton)] mb-3" />
                <div className="h-7 w-16 rounded bg-[var(--color-skeleton-light)] mb-2" />
                <div className="h-2 w-24 rounded bg-[var(--color-skeleton-light)]" />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="card p-5">
              <div className="h-4 w-40 rounded bg-[var(--color-skeleton)] mb-4" />
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i}>
                    <div className="h-3 w-28 rounded bg-[var(--color-skeleton-light)] mb-1.5" />
                    <div className="h-6 rounded bg-[var(--color-skeleton-light)]" style={{ width: `${80 - i * 25}%` }} />
                  </div>
                ))}
              </div>
            </div>
            <div className="card p-5">
              <div className="h-4 w-32 rounded bg-[var(--color-skeleton)] mb-4" />
              <div className="space-y-3">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded bg-[var(--color-skeleton-light)]" />
                    <div className="flex-1">
                      <div className="h-3 w-32 rounded bg-[var(--color-skeleton-light)]" />
                    </div>
                    <div className="h-3 w-12 rounded bg-[var(--color-skeleton-light)]" />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="card p-5">
            <div className="h-4 w-32 rounded bg-[var(--color-skeleton)] mb-4" />
            {[...Array(4)].map((_, i) => (
              <div key={i} className="flex items-center gap-3 py-3" style={{ borderBottom: "1px solid var(--color-border)" }}>
                <div className="w-6 h-6 rounded bg-[var(--color-skeleton-light)]" />
                <div className="flex-1 h-3 rounded bg-[var(--color-skeleton-light)]" />
                <div className="w-16 h-3 rounded bg-[var(--color-skeleton-light)]" />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <KpiCard
              label={translate("totalFiles") || "Total Files"}
              value={totalFiles}
              icon={FolderOpen}
              onClick={() => navigate("/files/general_information/upload")}
            />
            <KpiCard
              label={translate("totalProducts") || "Total products"}
              value={productsVectors.toLocaleString()}
              icon={Database}
              onClick={() => navigate("/products")}
            />
            <KpiCard
              label={translate("activeStoresLabel") || "Active stores"}
              value={`${stores.active} / ${stores.total}`}
              icon={MapPin}
              onClick={() => navigate("/store-locations")}
            />
            {isEffectivelyAdmin() && (
              <KpiCard
                label={translate("ingestionStatusLabel") || "Ingestion status"}
                value={
                  ingestionStatus.status
                    ? translate(ingestionStatus.status) || ingestionStatus.status
                    : translate("idle") || "Idle"
                }
                status={
                  ingestionStatus.status === "completed"
                    ? "success"
                    : ingestionStatus.status === "failed"
                    ? "error"
                    : "neutral"
                }
                icon={Activity}
                onClick={() => navigate("/products")}
              />
            )}
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            {/* Files by Collection */}
            <div className="card p-5">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>
                  {translate("filesByCollection") || "Files by collection"}
                </h3>
                <button
                  onClick={() => navigate("/files/general_information/upload")}
                  className="text-xs font-medium flex items-center gap-1 hover:opacity-80 transition-opacity"
                  style={{ color: "var(--color-mint)" }}
                >
                  {translate("viewAll") || "View All"}
                  <ArrowRight size={12} />
                </button>
              </div>
              <div className="space-y-4">
                {Object.entries(filesByCollection)
                  .sort(([, a], [, b]) => b - a)
                  .map(([col, count]) => {
                    const pct = (count / maxFilesByCol) * 100;
                    const label = collectionLabels[col] || col.replace(/_/g, " ");
                    return (
                      <div key={col}>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-xs font-medium capitalize" style={{ color: "var(--color-text-secondary)" }}>
                            {label}
                          </span>
                          <span className="text-xs font-semibold" style={{ color: "var(--color-text-primary)" }}>
                            {count}
                          </span>
                        </div>
                        <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: "var(--color-surface-raised)" }}>
                          <div
                            className="h-full rounded-full transition-all duration-700"
                            style={{
                              width: `${pct}%`,
                              backgroundColor: "var(--color-mint)",
                              opacity: 0.7 + (pct / 100) * 0.3,
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                {Object.keys(filesByCollection).length === 0 && (
                  <div className="flex items-center justify-center h-36">
                    <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>
                      {translate("noFilesYet") || "No files uploaded yet"}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* File Types */}
            <div className="card p-5">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>
                  {translate("fileTypes") || "File types"}
                </h3>
                <TrendingUp size={14} style={{ color: "var(--color-text-muted)" }} />
              </div>
              {Object.keys(filesByType).length > 0 ? (
                <div style={{ display: "flex", alignItems: "flex-end", gap: "12px", height: "144px" }}>
                  {Object.entries(filesByType)
                    .sort(([, a], [, b]) => b - a)
                    .map(([type, count]) => {
                      const pct = (count / maxFilesByType) * 100;
                      const barHeight = Math.max((pct / 100) * 110, 12);
                      return (
                        <div key={type} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", height: "100%" }}>
                          <span className="text-xs font-semibold" style={{ color: "var(--color-text-primary)", marginBottom: "6px" }}>
                            {count}
                          </span>
                          <div
                            style={{
                              width: "100%",
                              height: `${barHeight}px`,
                              borderRadius: "6px 6px 0 0",
                              backgroundColor: "var(--color-mint)",
                              opacity: 0.6 + (pct / 100) * 0.4,
                              transition: "height 0.7s ease",
                            }}
                          />
                          <span
                            className="text-xs font-medium uppercase"
                            style={{ color: "var(--color-text-muted)", fontSize: "10px", marginTop: "6px" }}
                          >
                            .{type}
                          </span>
                        </div>
                      );
                    })}
                </div>
              ) : (
                <div className="flex items-center justify-center h-36">
                  <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>
                    {translate("noFilesYet") || "No files uploaded yet"}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Bottom Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Recent Files */}
            <div className="card p-5 lg:col-span-2">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>
                  {translate("recentFiles") || "Recent Files"}
                </h3>
                <button
                  onClick={() => navigate("/files/general_information/upload")}
                  className="text-xs font-medium flex items-center gap-1 hover:opacity-80 transition-opacity"
                  style={{ color: "var(--color-mint)" }}
                >
                  {translate("viewAll") || "View All"}
                  <ArrowRight size={12} />
                </button>
              </div>
              {recentFiles.length > 0 ? (
                <div>
                  <div
                    className="grid gap-4 px-3 py-2 text-xs font-medium uppercase tracking-wider"
                    style={{
                      gridTemplateColumns: "1fr 100px 80px 120px 90px",
                      color: "var(--color-text-muted)",
                      fontSize: "10px",
                    }}
                  >
                    <span>{translate("fileName") || "File"}</span>
                    <span>{translate("collection") || "Collection"}</span>
                    <span>{translate("status") || "Status"}</span>
                    <span>{translate("modifiedBy") || "Modified by"}</span>
                    <span className="text-right">{translate("date") || "Date"}</span>
                  </div>
                  <div style={{ borderTop: "1px solid var(--color-border)" }}>
                    {recentFiles.map((file, i) => {
                      const isUpdated = file.last_updated_at && file.created_at && Math.abs(file.last_updated_at - file.created_at) > 60;
                      return (
                        <div
                          key={file.path || i}
                          className="grid gap-4 px-3 py-2.5 items-center transition-colors rounded-lg"
                          style={{
                            gridTemplateColumns: "1fr 100px 80px 120px 90px",
                            borderBottom: i < recentFiles.length - 1 ? "1px solid var(--color-border)" : "none",
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-surface-raised)")}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                        >
                          <div className="flex items-center gap-2.5 min-w-0">
                            <FileText size={14} style={{ color: "var(--color-text-muted)", flexShrink: 0 }} />
                            <span className="text-xs font-medium truncate" style={{ color: "var(--color-text-primary)" }}>
                              {file.name}
                            </span>
                          </div>
                          <span className="text-xs capitalize" style={{ color: "var(--color-text-secondary)" }}>
                            {collectionLabels[file.collection_name]?.split(" ")[0] || file.collection_name?.replace(/_/g, " ") || "—"}
                          </span>
                          <span
                            className="text-xs font-medium px-1.5 py-0.5 rounded-full text-center"
                            style={{
                              backgroundColor: isUpdated ? "rgba(37,99,235,0.08)" : "rgba(22,163,74,0.08)",
                              color: isUpdated ? "#3b82f6" : "#16a34a",
                              fontSize: "10px",
                            }}
                          >
                            {isUpdated ? (translate("updated") || "Updated") : (translate("created") || "Created")}
                          </span>
                          <span
                            className="text-xs truncate cursor-default"
                            style={{ color: "var(--color-text-secondary)" }}
                            title={file.created_by || ""}
                          >
                            {file.created_by || "—"}
                          </span>
                          <span className="text-xs text-right" style={{ color: "var(--color-text-muted)" }}>
                            {formatDate(file.modified ? file.modified * 1000 : null)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-48">
                  <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>
                    {translate("noFilesUploaded") || "No files uploaded yet"}
                  </p>
                </div>
              )}
            </div>

            {/* System Status Panel */}
            <div className="card p-5">
              {isEffectivelyAdmin() && (
                <>
                  <h3 className="text-sm font-semibold mb-4" style={{ color: "var(--color-text-primary)" }}>
                    {translate("ingestion") || "Ingestion"}
                  </h3>
                  <div className="space-y-4 mb-4">
                    <StatusRow
                      label={translate("scheduler") || "Scheduler"}
                      value={schedulerRunning ? (translate("running") || "Running") : (translate("stopped") || "Stopped")}
                      status={schedulerRunning ? "success" : "error"}
                    />
                    <StatusRow
                      label={translate("ingestion") || "Ingestion"}
                      value={
                        ingestionStatus.status
                          ? translate(ingestionStatus.status) || ingestionStatus.status
                          : translate("idle") || "Idle"
                      }
                      status={
                        ingestionStatus.status === "completed"
                          ? "success"
                          : ingestionStatus.status === "failed"
                          ? "error"
                          : "neutral"
                      }
                    />
                    <StatusRow
                      label={translate("nextRun") || "Next Run"}
                      value={
                        data?.scheduler?.next_run_time
                          ? new Date(data.scheduler.next_run_time).toLocaleString(undefined, {
                              day: "2-digit",
                              month: "short",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "—"
                      }
                      status="neutral"
                    />
                  </div>
                </>
              )}

              <div className={isAdmin() ? "space-y-4" : "space-y-4"}>
                {/* Stores mini */}
                <div style={{ borderTop: "1px solid var(--color-border)", paddingTop: "16px" }}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-medium" style={{ color: "var(--color-text-secondary)" }}>
                      {translate("storeLocations") || "Store Locations"}
                    </span>
                    <button
                      onClick={() => navigate("/store-locations")}
                      className="text-xs font-medium hover:opacity-80 transition-opacity"
                      style={{ color: "var(--color-mint)" }}
                    >
                      <ArrowRight size={12} />
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <MiniStat value={stores.total} label={translate("total") || "Total"} icon={MapPin} iconColor="var(--color-mint)" />
                    <MiniStat value={stores.active} label={translate("active") || "Active"} icon={Power} iconColor="var(--color-success)" />
                    <MiniStat value={stores.inactive} label={translate("inactive") || "Inactive"} icon={PowerOff} iconColor="var(--color-text-muted)" />
                  </div>
                </div>

                {/* Quick links */}
                <div style={{ borderTop: "1px solid var(--color-border)", paddingTop: "16px" }}>
                  <button
                    onClick={() => navigate("/similarity-search")}
                    className="w-full flex items-center gap-2.5 p-2.5 rounded-lg transition-colors"
                    style={{ backgroundColor: "var(--color-surface-raised)" }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-mint-muted)")}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-surface-raised)")}
                  >
                    <Search size={14} style={{ color: "var(--color-mint)" }} />
                    <span className="text-xs font-medium" style={{ color: "var(--color-text-primary)" }}>
                      {translate("similaritySearch") || "Similarity Search"}
                    </span>
                    <ArrowRight size={12} className="ml-auto" style={{ color: "var(--color-text-muted)" }} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

/* ─── Sub-components ─── */

const KpiCard = ({ label, value, icon: Icon, status, onClick }) => {
  const statusColor =
    status === "success"
      ? "var(--color-success)"
      : status === "error"
      ? "var(--color-error)"
      : "var(--color-mint)";

  return (
    <button
      onClick={onClick}
      className="card p-5 text-left transition-all duration-200 hover:shadow-md group"
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-medium" style={{ color: "var(--color-text-muted)" }}>
          {label}
        </span>
        <Icon size={14} style={{ color: "var(--color-text-muted)" }} />
      </div>
      <p className="text-xl font-semibold" style={{ color: status ? statusColor : "var(--color-text-primary)" }}>
        {value}
      </p>
    </button>
  );
};

const StatusRow = ({ label, value, status }) => {
  const dotColor =
    status === "success"
      ? "var(--color-success)"
      : status === "error"
      ? "var(--color-error)"
      : "var(--color-text-muted)";

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: dotColor }} />
        <span className="text-xs font-medium" style={{ color: "var(--color-text-secondary)" }}>
          {label}
        </span>
      </div>
      <span className="text-xs font-medium capitalize" style={{ color: "var(--color-text-primary)" }}>
        {value}
      </span>
    </div>
  );
};

const MiniStat = ({ value, label, icon: Icon, iconColor }) => (
  <div
    className="py-2 px-1 rounded-lg"
    style={{ backgroundColor: "var(--color-surface-raised)" }}
  >
    {Icon && <Icon size={10} className="mx-auto mb-1" style={{ color: iconColor }} />}
    <p className="text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>
      {value}
    </p>
    <p className="text-xs" style={{ color: "var(--color-text-muted)", fontSize: "10px" }}>
      {label}
    </p>
  </div>
);

const PersonalizedLanding = () => {
  const { translate } = useLanguage();
  const { canSeeSection, isManager, internalProceduresAccess } = useAuth();
  const navigate = useNavigate();

  // internal_procedures access (manager, or a department member with a bound
  // folder) is resolved once in AuthContext and shared with the sidebar, so Home
  // never advertises an area that would be empty and read-only for the user.
  const ipChecking = internalProceduresAccess === "loading";

  const shortcuts = [];
  if (canSeeSection(SECTIONS.GENERAL_INFORMATION)) {
    shortcuts.push({
      key: "general_information",
      label: translate("generalInformation") || "General Information",
      icon: FolderOpen,
      path: "/files/general_information",
    });
  }
  if (canSeeSection(SECTIONS.CAMPAIGNS)) {
    shortcuts.push({
      key: "campaigns",
      label: translate("campaigns") || "Campaigns",
      icon: Megaphone,
      path: "/files/campaigns",
    });
  }
  if (internalProceduresAccess === true) {
    shortcuts.push({
      key: "internal_procedures",
      label: translate("internalProcedures") || "Internal Procedures",
      icon: ShieldCheck,
      path: "/files/internal_procedures",
    });
  }
  // O mesmo `anyOf` da entrada da barra lateral e da rota (Sidebar NAV_MAIN,
  // App.js RoleRoute): quem tem so o papel `conversations` via a barra lateral
  // com Conversas a funcionar e esta pagina, que e o primeiro ecra depois de
  // entrar, a dizer que nao tinha acesso a area nenhuma.
  if (canSeeSection(CONVERSATIONS) || isManager()) {
    shortcuts.push({
      key: "conversations",
      label: translate("conversations") || "Conversations",
      icon: MessagesSquare,
      path: "/conversations",
    });
  }
  // O mesmo `anyOf` da rota e da barra lateral (App.js, Sidebar NAV_MAIN).
  if (canSeeSection(ANALYTICS) || isManager()) {
    shortcuts.push({
      key: "analytics",
      label: translate("analytics") || "Analytics",
      icon: LineChart,
      path: "/analytics",
    });
  }
  if (isManager()) {
    shortcuts.push({
      key: "configs",
      label: translate("configurations") || "Configurations",
      icon: Sliders,
      path: "/configs",
    });
    shortcuts.push({
      key: "widget",
      label: translate("widget") || "Widget",
      icon: Palette,
      path: "/widget",
    });
    shortcuts.push({
      key: "store-locations",
      label: translate("storeLocations") || "Store Locations",
      icon: MapPin,
      path: "/store-locations/list",
    });
  }

  return (
    <div className="page-enter">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold" style={{ color: "var(--color-text-primary)" }}>
          {translate("welcomeBack") || "Welcome back"}
        </h1>
        <p className="text-sm mt-1" style={{ color: "var(--color-text-secondary)" }}>
          {translate("personalLandingDescription") || "Quick access to the areas you manage"}
        </p>
      </div>

      {shortcuts.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {shortcuts.map(({ key, label, icon: Icon, path }) => (
            <button
              key={key}
              onClick={() => navigate(path)}
              className="card p-5 text-left transition-all duration-200 hover:shadow-md group"
            >
              <div className="flex items-center justify-between mb-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: "var(--color-mint-muted)" }}
                >
                  <Icon size={20} style={{ color: "var(--color-mint)" }} />
                </div>
                <ArrowRight size={14} style={{ color: "var(--color-text-muted)" }} />
              </div>
              <p className="text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>
                {label}
              </p>
            </button>
          ))}
        </div>
      ) : ipChecking ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="card p-5 h-24 animate-pulse bg-[var(--color-skeleton)]" />
        </div>
      ) : (
        <div className="card p-8 text-center">
          <AlertCircle size={24} className="mx-auto mb-3" style={{ color: "var(--color-text-muted)" }} />
          <p className="text-sm" style={{ color: "var(--color-text-secondary)" }}>
            {translate("noAccessibleSections") ||
              "You don't have access to any management area yet. Contact your administrator."}
          </p>
        </div>
      )}
    </div>
  );
};

export default Homepage;
